---
title: Roadmap
description: What audio/ui ships today, and what comes next.
---

**audio/ui** is a toolkit for building audio interfaces in React: headless primitives on npm (`@audio-ui/react`) and styled components for [shadcn/ui](https://ui.shadcn.com/) that you copy, paste and customize.

## Current Status

Available today:

### Elements

The pieces of any audio interface:

- **Controls**: Knob, Fader, XY Pad and Envelope, with scales, rulers and fine drag
- **Keyboards and pads**: Piano and Pad
- **Displays**: Meter and Needle meter (a VU dial to the standard), Waveform, from peaks, a live signal or a range of the audio, Plot, for curves and draggable points, Spectrum analyzer and Tuner

### Workstation

The surfaces of a DAW, controlled and independent of any audio engine:

- **Arrangement**: Timeline, a zoomable ruler with lanes, ranges and a navigator, and the arrangement editor on top of it: tracks of clips, selection, time commands, mixes, tempo, signatures, locators, loop and undo
- **Editors**: Piano Roll, with scales, edits, lanes and a MIDI file in and out, and Sequencer
- **Piano roll tools**: Scale menu, Note find and Note edit menu
- **Transport**: Transport and Scrubber
- **Mixing**: Mixer, and the device chain: a track's devices in a row, each drawn by its own panel, with a library to add one from

### Devices

22 effects and instruments, each a definition, a Web Audio engine and a panel generated from its parameters: dynamics (compressor, multiband, limiter, gate, de-esser, transient shaper), tone (equalizers and a filter), distortion, space and modulation (delay, reverb, chorus, phaser, auto pan), and utility and two instruments. A device reports what it takes off the signal for a meter, and exposes tools for an agent. See [Devices](/docs/devices).

### Audio engine

The hooks and libraries on the Web Audio API that make the sound, for the components that need it: output, clock and time, synthesis, input and analysis, and the controllers of `@audio-ui/core` for use with no framework. An engine of your own can drive the same components. See [Audio engine](/docs/engine).

### Blocks

Pre-assembled patterns: a mixing console, a drum rack, a piano roll, a timeline, a waveform player, and a block for each device. Browse the [component catalog](/components).

### For agents

- **[MCP server](/docs/mcp)**: list components, read their docs and search the catalog from your agent
- **[Agent skill](/docs/agents)**: teaches your agent which layer to pick and how to wire each component

## What's Next

- **Adapters beyond React**: the controllers of `@audio-ui/core` already drive a control with no framework, so the next step is to package that: custom elements over the controllers, and Vue and Svelte adapters, each one kept in step with the React primitives. How many to maintain is still to decide. A page would show one knob and one fader in React, Vue, Svelte and a web component, all on the same store and in sync.
- **Agents**: the Agent Skill and the MCP server have a link on the home page and a page each. They should be easier to find, from the documentation overview and from the component pages, with what an agent gets from each in a few lines.
- **Devices**: a device is a definition, an engine and a panel, and the panel is generated from the parameters alone, with no code of its own per device. That is shown on the Devices page as one device in two skins; it should be shown as the idea it is, with the contract (the parameters, the readings, the tools for an agent) in plain view.
- **More audio components**: further audio-specific patterns and controls.

## Contributing

We're building this in the open and welcome contributions! Whether it's bug reports, feature requests, or code contributions, we'd love to have you involved.

Check out our [GitHub repository](https://github.com/ouestlabs/audio-ui) to get started.
